/**
 * @file        src/lib/fareRuleSummary.js
 * @description Reduce a TripJack fare-rule response (POST /fare-rule) to the few
 *              facts a fare card needs: the cheapest cancellation fee, the
 *              cheapest date-change fee, the cut-off after which a cancellation
 *              becomes a no-show, and whether seats are chargeable.
 *
 *              Real response shape (verified against the live sandbox):
 *                fareRule: {
 *                  "BOM-DEL": {
 *                    fr: {},
 *                    tfr: {
 *                      NO_SHOW:     [{ st:"0",  et:"5" }],
 *                      DATECHANGE:  [{ amount:3250, additionalFee:100, st:"5", et:"74" }, …],
 *                      CANCELLATION:[{ amount:3500, additionalFee:115, st:"5", et:"74" }, …],
 *                      SEAT_CHARGEABLE: [{ st:"0", et:"8760" }],
 *                    }
 *                  }
 *                }
 *              `st`/`et` are HOURS before scheduled departure. A fee is the
 *              window's `amount` + `additionalFee`. Every field is optional —
 *              anything we cannot read stays `null` so the UI shows a neutral
 *              "as per fare rules" line rather than a wrong number.
 * @author      Kinetika Labs
 * @created     2026-07-09
 * @changelog
 *   - 2026-09-11 Claude — (3) Adds `sectors`: EVERY sector of the rule with its
 *                          complete CANCELLATION / DATECHANGE slab lists (st, et,
 *                          amount, addFee, policy) and its SEAT_CHARGEABLE text,
 *                          plus a top-level `seat`. The fare card prints these
 *                          as-is. Existing fields are unchanged.
 *   - 2026-09-11 Claude — (2) TripJack's zero-fee shape — `amount` omitted but
 *                          `additionalFee` present, policy "cancellation" /
 *                          "Reissue" — now reads as a ₹0 airline fee, matching
 *                          TripJack's own "₹0.00 cancellation". Cut-off hours come
 *                          from the earliest cancellation window's `st` (live
 *                          NO_SHOW is 0→8760, which printed "up to 8760 hours").
 *   - 2026-09-11 Claude — Each category's cheapest window is now returned as its
 *                          parts ({ amount, addFee, total }) under `cancel` /
 *                          `dateChange`, so the fare card can print the airline fee
 *                          and the service fee separately ("₹3,000.00 + ₹115.00")
 *                          and tell an explicit ₹0 from a missing amount.
 *                          `cancelFrom` / `dateChangeFrom` remain as the totals.
 *   - 2026-07-09 Claude — Initial.
 */

const num = (v) => { const n = Number(v); return Number.isFinite(n) ? n : null; };

// Case/punctuation-insensitive key match ("NO_SHOW" → "noshow").
const norm = (s) => String(s || '').toLowerCase().replace(/[^a-z]/g, '');

// Find the category bucket (an array of policy windows) inside `tfr`.
function bucket(tfr, ...fragments) {
  const hit = Object.entries(tfr || {}).find(([k]) => fragments.some((f) => norm(k).includes(f)));
  const v = hit ? hit[1] : null;
  return Array.isArray(v) ? v : (Array.isArray(v?.policyInfos) ? v.policyInfos : []);
}

// One window's fee as its two real parts: `amount` is the airline's own charge
// (fcs.ACF / ARF), `additionalFee` the service fee added on top (fcs.CCF / …).
//
// TripJack's ZERO-FEE shape omits `amount` but still sends `additionalFee`:
// { additionalFee: 0, fcs: {}, policyInfo: "cancellation" | "Reissue" }. Across
// 1,243 logged fare rules all 278 + 278 such windows carry exactly that bare
// policy, and TripJack's own site prints them as "₹0.00 cancellation" /
// "₹0.00 Reissue" (ECO VALUE / ECO CLASSIC / ECO FLEX / PUBLISHED, BOM-DEL) — so
// the airline amount is 0. A window with NEITHER field is a different shape —
// "Refundable subject to cancellation penalty" — and stays null: unknown, never
// read as free.
const windowParts = (w) => {
  const rawAmount = w?.amount ?? w?.amt;
  const hasAddFee = w != null && typeof w === 'object' && ('additionalFee' in w || 'addFee' in w);
  const amount = rawAmount != null ? num(rawAmount) : (hasAddFee ? 0 : null);
  if (amount == null) return null;
  const addFee = num(w?.additionalFee ?? w?.addFee) || 0;
  return { amount, addFee, total: amount + addFee };
};

// Cheapest window across a category ("fee starts at ₹X"), kept as its parts so
// the card can print "₹3,000.00 + ₹115.00" instead of a merged ₹3,115.
function cheapest(windows) {
  const all = (windows || []).map(windowParts).filter(Boolean);
  return all.length ? all.reduce((a, b) => (b.total < a.total ? b : a)) : null;
}

// Airline policy text without TripJack's markup: "__nls__" is a line break and
// "__bs__" a bullet. The first non-empty line carries the rule itself; what
// follows is boilerplate ("Please Note: Fare rules are subject to change…").
function cleanPolicy(p) {
  return String(p || '').split('__nls__').map((x) => x.replace(/__bs__/g, '').trim()).find(Boolean) || '';
}

/**
 * Every window of a category as a display slab, in the API's own order (it is
 * ascending `st` in every logged response). A Slab is
 *   { st, et, amount, addFee, total, policy }
 * — st/et in hours before departure; amount/total null when the airline sent no
 * amount (see windowParts for TripJack's ₹0 shape); policy = cleaned policyInfo.
 */
function slabs(windows) {
  return (windows || []).map((w) => {
    const parts = windowParts(w);
    return {
      st: num(w?.st),
      et: num(w?.et),
      amount: parts ? parts.amount : null,
      addFee: parts ? parts.addFee : (num(w?.additionalFee) || 0),
      total: parts ? parts.total : null,
      policy: cleanPolicy(w?.policyInfo),
    };
  });
}

/**
 * @param {object} raw  Raw /fare-rule response.
 * @returns {{sectors:Array<{sector:string, cancel:Slab[], dateChange:Slab[],
 *              seat:{text:string, amount:number|null, addFee:number}|null}>,
 *            seat:{text:string, amount:number|null, addFee:number}|null,
 *            cancel:{amount:number, addFee:number, total:number}|null,
 *            dateChange:{amount:number, addFee:number, total:number}|null,
 *            cancelFrom:number|null, dateChangeFrom:number|null, cutoffHrs:number|null,
 *            seatsChargeable:boolean|null, sector:string}|null}
 */
export function summarizeFareRule(raw) {
  const root = raw?.fareRule || raw?.data?.fareRule;
  if (!root || typeof root !== 'object') return null;

  // A one-way fare has a single sector; take the first route-keyed entry.
  const entry = Object.entries(root).find(([k]) => /^[A-Z]{3}-[A-Z]{3}$/.test(k)) || Object.entries(root)[0];
  if (!entry) return null;
  const [sector, node] = entry;
  const tfr = node?.tfr || node?.fr || node;
  if (!tfr || typeof tfr !== 'object') return null;

  const cancellation = bucket(tfr, 'cancel');
  const dateChangeWins = bucket(tfr, 'datechange', 'reissue');
  const noShow = bucket(tfr, 'noshow');
  const seatKey = Object.keys(tfr).some((k) => norm(k).includes('seat'));
  const seatRows = bucket(tfr, 'seat');

  // Cancellation cut-off = the EARLIEST cancellation window's start hour. The
  // windows run st → et hours before departure, so the lowest `st` is the last
  // moment a cancellation is accepted — TripJack's site prints it as "4 hrs to
  // 365 days". The no-show window's END hour is now only a fallback: it agreed on
  // the sandbox shape (NO_SHOW 0→5 beside windows from 5), but live fares send
  // NO_SHOW 0→8760, which printed "(up to 8760 hours before departure)".
  const cancelStarts = cancellation.map((w) => num(w.st)).filter((n) => n != null);
  const cutoffHrs = cancelStarts.length ? Math.min(...cancelStarts) : num(noShow?.[0]?.et);

  const cancel = cheapest(cancellation);
  const dateChange = cheapest(dateChangeWins);

  // EVERY sector the rule covers — a combo / connecting fare carries 2–3 (e.g.
  // BOM-DXB + DXB-BOM) — each with its complete slab lists: what the fare card
  // prints. The fields above stay first-sector only, as before.
  const sectors = Object.entries(root)
    .filter(([, n]) => n && typeof n === 'object')
    .map(([sec, n]) => {
      const t = n.tfr || n.fr || n;
      // SEAT_CHARGEABLE: the first window that says something ("Paid Seat", …).
      const seatW = bucket(t, 'seat').find((w) => cleanPolicy(w?.policyInfo) || num(w?.amount) > 0) || null;
      return {
        sector: sec,
        cancel: slabs(bucket(t, 'cancel')),
        dateChange: slabs(bucket(t, 'datechange', 'reissue')),
        seat: seatW ? { text: cleanPolicy(seatW.policyInfo), amount: num(seatW.amount), addFee: num(seatW.additionalFee) || 0 } : null,
      };
    });

  return {
    sector,
    sectors,
    seat: sectors.map((s) => s.seat).find(Boolean) || null,
    // The cheapest window as its real parts — see cheapest().
    cancel,
    dateChange,
    // Totals, kept for ranking ("Recommended" = lowest cancellation fee).
    cancelFrom: cancel ? cancel.total : null,
    dateChangeFrom: dateChange ? dateChange.total : null,
    cutoffHrs: Number.isFinite(cutoffHrs) ? cutoffHrs : null,
    // The category's PRESENCE means the airline charges for seats on this fare.
    // Absent entirely → seats are free. Present but empty → unknown.
    seatsChargeable: seatKey ? (seatRows.length > 0 ? true : null) : false,
  };
}
